import React, { useRef, useCallback, useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  FlaskConical,
  Pill,
  ShieldCheck,
  Shield,
  Eye,
  Settings,
  Heart,
  AlertTriangle,
  LogOut,
  MessageSquare,
  Mail,
  Brain,
  BarChart3,
  Receipt,
  ContactRound,
  Handshake,
  BotMessageSquare,
  Calendar,
  Bell,
  Download,
  Globe,
  Key,
  Stethoscope,
  Cloud,
  Plug,
  CreditCard,
  FileBarChart,
  Microscope,
  FileText,
  Dna,
  Video,
  ShieldAlert,
  ClipboardCheck,
  FileSearch,
  UserCog,
  UserCheck,
  CalendarOff,
  Scale,
  Lock,
  UploadCloud,
  Smartphone,
  Inbox,
  FileSignature,
  Truck,
  Activity,
  Package,
} from 'lucide-react';
import logo from '../assets/logo.svg';

import { versionLine, APP_BUILT_AT } from "../lib/appVersion";
// ── Icon lookup map (since we can't serialize React components) ──────────────
const ICON_MAP = {
  LayoutDashboard, Users, ClipboardList, FlaskConical, Pill, ShieldCheck, Shield, Eye,
  Settings, Heart, AlertTriangle, MessageSquare, Mail, Brain, BarChart3,
  Receipt, ContactRound, Handshake, BotMessageSquare, Calendar, Bell, Download,
  Globe, Key, Stethoscope, Cloud, Plug, CreditCard, FileBarChart,
  Microscope, FileText, Dna, Video, ShieldAlert, ClipboardCheck, FileSearch, UserCog, CalendarOff, Scale, Lock, UploadCloud, Smartphone, Inbox, FileSignature, Truck, Activity, Package, UserCheck,
};

// ── Default section layout ──────────────────────────────────────────────────
//
// ⚠ Exported at the bottom of this file as DEFAULT_SECTIONS_FOR_TEST — Card
// P114 (F4). The reorder test asserts the RENDERED label order, which is the
// only order that matters; it ALSO needs the declared ids and permissions to
// prove a reorder neither dropped an entry nor loosened a gate, and those two
// facts are invisible to a render taken by an admin who can see everything.
const DEFAULT_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', iconName: 'LayoutDashboard', permission: 'dashboard:read' },
    ],
  },
  {
    label: 'Clinical',
    // Card P114 (F4) — Luisa's workflow order, rendered as written.
    // See the `section.ordered` comment at the render site.
    ordered: true,
    items: [
      { id: 'patients', label: 'Patients', iconName: 'Users', permission: 'patients:read' },
      // Physician Dashboard (Task #62/#63, migration 100) — primary surface
      // for the physician role. Consult queue + candidacy verdicts + safety
      // screen + treatment plans. Carlos's spec: "The Physician Diagnoses.
      // The PEM Coordinates."
      { id: 'physiciandashboard', label: 'Physician Dashboard', iconName: 'Stethoscope', permission: 'physician:read' },
      // PEM Dashboard (Task #51, migration 098) — primary surface for the
      // patient_experience role. 15-stage journey lifecycle Kanban,
      // follow-up cadence queue, KPI rollups.
      { id: 'pemdashboard', label: 'Patient Experience', iconName: 'ContactRound', permission: 'pem:read' },
      // Clinical Documentation (Task #40): SOAP/H&P/Discharge/Procedure/Progress notes
      // with voice dictation + Bedrock AI scribe + e-signature + addenda + Route for
      // Review + Export PDF. Separate from the legacy 'encounters' surface above.
      { id: 'clinicaldocs', label: 'Clinical Documentation', iconName: 'FileSignature', permission: 'notes:read' },
      { id: 'medications', label: 'Medications', iconName: 'Pill', permission: 'medications:read' },
      { id: 'labs', label: 'Lab Results', iconName: 'FlaskConical', permission: 'labs:read' },
      { id: 'allergies', label: 'Allergies', iconName: 'AlertTriangle', permission: 'allergies:read' },
      { id: 'vitals', label: 'Vitals', iconName: 'Heart', permission: 'vitals:read' },
      // Clinic Tab — Treatment Day Dashboard (Dr. Luis Phase A, migration 119).
      // Physician-only clinical view: diagnosis/risk/therapy/allergies/meds + one-page order sheet.
      { id: 'clinictab', label: 'Clinic — Treatment Day', iconName: 'Activity', permission: 'physician:read' },
      { id: 'encounters', label: 'Encounters', iconName: 'ClipboardList', permission: 'encounters:read' },
      // Chief Medical Director oversight — all clinical-AI research across all
      // patients/doctors/clinics (Dr. Luis). Gated on clinical_oversight:read.
      { id: 'clinicaloversight', label: 'Clinical Oversight', iconName: 'Eye', permission: 'clinical_oversight:read' },    ],
  },
  {
    label: 'Communication',
    items: [
      // Unified Messages surface — collapses the old "Secure Chat" + "Secure Email"
      // entries into a single tabbed page. Both used the same TOTP_ENCRYPTION_KEY
      // AES-256-GCM envelope at rest, so the consolidation is structural; the
      // encryption posture is unchanged. Gated on chat:read OR email:read so
      // users with only one of those permissions still see the entry; the
      // inner tabs hide themselves based on what the user actually has.
      { id: 'messages', label: 'Messages', iconName: 'MessageSquare', permissions: ['chat:read', 'email:read'] },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { id: 'biolab', label: 'Research Repository', iconName: 'Dna', permission: 'bi:read' },
      // Restricted Protocol Library (Daniel/Dr. Luis) — content gated by an
      // explicit per-user grant; this entry just navigates to the page.
      { id: 'protocollibrary', label: 'Protocol Library', iconName: 'FlaskConical', permission: 'protocol_vault:view' },
      { id: 'bi', label: 'BI Dashboard', iconName: 'BarChart3', permission: 'bi:read' },
      { id: 'airesearch', label: 'AI Research', iconName: 'Brain', permission: 'ai_research:read' },
      { id: 'adminai', label: 'Admin AI', iconName: 'BotMessageSquare', permission: 'admin_ai:read' },
      { id: 'reports', label: 'Report Builder', iconName: 'FileSearch', permission: 'reports:read' },
      // CEO Dashboard (Carlos §4.7) — management-only; hidden entirely without
      // reports:management:read (same gate as the P1d Sales Rep Performance card).
      { id: 'ceo-dashboard', label: 'CEO Dashboard', iconName: 'FileBarChart', permission: 'reports:management:read' },
      // AI Alerts is now a tab inside the unified "Activity" inbox (in the
      // Operations section). Old route id 'aialerts' is preserved in
      // App.js as a redirect so existing bookmarks still work.
      { id: 'competitiveintel', label: 'Competitive Intel', iconName: 'Globe', permission: 'bi:read' },
    ],
  },
  {
    label: 'Operations',
    // Card P114 (F4) — Luisa's workflow order, rendered as written.
    // See the `section.ordered` comment at the render site.
    ordered: true,
    items: [
      { id: 'crm', label: 'ZignaGenix CRM', iconName: 'ContactRound', permission: 'crm:read' },
      { id: 'scheduling', label: 'Scheduling', iconName: 'Calendar', permission: 'scheduling:read' },
      // Unified Activity inbox — collapses the old "Notifications" + "AI Alerts"
      // entries and adds a "My Tasks" tab. Single unread badge across all
      // three sources. Permission gate is the broad notifications:read so any
      // user who saw Notifications before still sees Activity; the inner
      // tabs hide themselves based on alerts:read / crm:read.
      { id: 'activity', label: 'Activity', iconName: 'Inbox', permission: 'notifications:read' },
      { id: 'gohighlevel', label: 'GoHighLevel', iconName: 'Plug', permission: 'crm:read' },
      // Unified Revenue hub — collapses Billing, Estimates, Stripe Payments,
      // Stripe Reports, and Payment Analytics into one tabbed surface. Five
      // sidebar entries → one. Each legacy route ID is preserved as an
      // App.js redirect so existing bookmarks land on the matching tab.
      // Insurance and Scheduling stay separate (they have different RBAC
      // resources and conceptually belong to different workflows).
      { id: 'revenue', label: 'Revenue', iconName: 'Receipt', permission: 'billing:read' },
      // Logistics dashboard (Phase 5l-c, migration 112) — by-clinic Kanban
      // for product/travel/provider items + assigned-to-me queue + needed-by
      // deadlines. logistics role + cross-team read (physician/PEM/sysadmin).
      { id: 'logisticsdashboard', label: 'Logistics', iconName: 'Truck', permission: 'logistics:read' },
      // Alliance Partners (Task #69, Jun 9 2026) — Carlos's Strategic Alliance
      // Partner Program. Staff-side admin for partner orgs, tier history,
      // commission accruals, attribution audit. Visible to anyone with
      // alliance_partner:read (business, patient_experience, sysadmin).
      { id: 'alliancepartners', label: 'Alliance Partners', iconName: 'Handshake', permission: 'alliance_partner:read' },
      { id: 'insurance', label: 'Insurance', iconName: 'ClipboardCheck', permission: 'billing:read' },
      { id: 'crmimport', label: 'CRM Import/Upload', iconName: 'UploadCloud', permission: 'crm:create' },
      // Inventory (#152, Workstream 2 — migration 141) — internal central
      // catalog/lots/stock ledger + low-stock/near-expiry alerts + partner
      // supply-order fulfillment queue. inventory:read (sysadmin/business +
      // clinical read). Partner-facing supply surface is a SEPARATE portal.
      { id: 'inventory', label: 'Inventory', iconName: 'Package', permission: 'inventory:read' },
      // PEM Assignment (#348 interface) — who is accountable for each patient.
      // Gated on patients:reassign, which NO ROLE holds; Daniel grants it
      // per-user, so this entry is invisible until he does. The screen also
      // needs pem:read (to read the current owner) and crm:read (to search) —
      // see the PR body; a partial grant surfaces as a visible refusal, never
      // as a silent unguarded write.
      { id: 'pemAssignment', label: 'PEM Assignment', iconName: 'UserCheck', permission: 'patients:reassign' },
      // Executive Calendar Oversight (O-1, Carlos's ask) — company-at-a-glance
      // month heatmap of all-staff calendar load + rollups. calendar:oversight
      // is held by admin + board_of_directors only, so this entry is hidden
      // from all staff roles. Tier 1: counts + staff names, no patient data.
      { id: 'calendarOversight', label: 'Calendar Oversight', iconName: 'Activity', permission: 'calendar:oversight' },
      // Unified Telehealth entry — collapses the old separate "Telehealth"
      // (WebRTC live call surface, routes/voip.js) and "Telemedicine
      // Scheduling" (session admin, routes/telemedicine.js) into one
      // tabbed page. They were never duplicates — they're sequential
      // phases of the same encounter — but two sidebar items with
      // similar names created constant confusion about where to click.
      { id: 'telehealth', label: 'Telehealth', iconName: 'Video', permission: 'telemed:read' },
      // Unified Personnel hub — collapses Personnel, Leave Requests, and
      // Personnel Actions into one tabbed page. All three are HR workflows
      // (roster → time off → performance) that staff bounce between
      // constantly. Old route IDs preserved as App.js redirects.
      { id: 'personnel', label: 'Personnel', iconName: 'UserCog', permissions: ['personnel:read', 'leave:read'] },    ],
  },
  {
    label: 'Administration',
    items: [
      // Unified Access Control hub — collapses User Management, Role
      // Management, Permission Manager, and Sidebar Visibility into one
      // tabbed page. Four sidebar entries → one. Any-of permission gate
      // because Permission Manager requires users:update while the other
      // three only require users:read; users with either see the entry
      // and the inner tabs hide themselves per their narrower permission.
      { id: 'access', label: 'Access Control', iconName: 'Users', permissions: ['users:read', 'users:update'] },
      { id: 'adminaudit', label: 'Admin Audit Trail', iconName: 'ShieldCheck', permission: 'audit:read' },
      // Card P244 — the in-CRM feedback queue. feedback:triage is held by
      // sysadmin and admin only; every other role submits but never reads
      // somebody else's report, so this entry is invisible to them.
      { id: 'feedbacktriage', label: 'Feedback Triage', iconName: 'MessageSquare', permission: 'feedback:triage' },
      { id: 'compliance', label: 'Privacy & Compliance', iconName: 'ShieldAlert', permission: 'admin:read' },
      // Compliance & Freshness Watchdog admin dashboard (Task #34, commit 2457186).
      // Drift detection across infrastructure, supply chain, schema, secrets, AI models.
      // Sibling to Security Engine (AI) below; together they form the platform's
      // complete drift + posture surface.
      { id: 'complianceposture', label: 'Compliance Posture', iconName: 'ClipboardCheck', permission: 'compliance:read' },
      { id: 'intelligence', label: 'Intelligence Engines', iconName: 'Brain', permission: 'model_eval:read' },
      { id: 'dataexport', label: 'Data Export', iconName: 'Download', permission: 'export:clinical' },
      { id: 'portal', label: 'Patient Portal Admin', iconName: 'Globe', permission: 'portal:manage' },
      { id: 'patientportal', label: 'Patient Portal View', iconName: 'Stethoscope', permission: 'portal:manage' },
      { id: 'totp', label: '2FA Setup', iconName: 'Key', permission: 'totp:setup' },
      { id: 'awscloud', label: 'AWS Cloud', iconName: 'Cloud', permission: 'settings:update' },
      // Security AI Engine admin dashboard (Tasks #35/#36 — commits 334ce42 / 91efc0b).
      // Daily npm-audit + AWS Health + ECR + AWS-config-drift + codebase-regex scans
      // with Bedrock AI triage. Human-in-the-loop disposition buttons.
      { id: 'securityengine', label: 'Security Engine (AI)', iconName: 'ShieldCheck', permission: 'security:read' },
      { id: 'securityupdates', label: 'Security Updates', iconName: 'ShieldAlert', permission: 'settings:read' },
      { id: 'mobilemanagement', label: 'Mobile Management', iconName: 'Smartphone', permission: 'users:read' },
      { id: 'audit', label: 'Audit Log', iconName: 'ShieldCheck', permission: 'audit:read' },
      { id: 'settings', label: 'Settings', iconName: 'Settings', permission: 'settings:read' },
    ],
  },
];

function LogoutConfirmModal({ onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000,
    }} onClick={onCancel}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '2rem',
        width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        textAlign: 'center',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', background: '#fef2f2',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1rem',
        }}>
          <LogOut size={24} color="#DC3545" />
        </div>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#111827' }}>
          Confirm Sign Out
        </h3>
        <p style={{ margin: '0 0 1.5rem', color: '#6C757D', fontSize: '0.875rem', lineHeight: 1.5 }}>
          Are you sure you want to sign out? Any unsaved changes will be lost.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button onClick={onCancel} style={{
            padding: '0.5rem 1.25rem', border: '1px solid #dee2e6',
            borderRadius: 8, background: '#fff', color: '#374151',
            fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '0.5rem 1.25rem', border: 'none',
            borderRadius: 8, background: '#DC3545', color: '#fff',
            fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
          }}>Sign Out</button>
        </div>
      </div>
    </div>
  );
}

// ── Activity unread badge ───────────────────────────────────────────────────
// Polls the unified activity:unread roll-up so the sidebar shows one count
// across notifications + AI alerts + due tasks. Silent on failure — a missing
// badge is better UX than a broken sidebar if the network blips.
function ActivityBadge() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let interval = null;

    const poll = async () => {
      const api = (typeof window !== 'undefined') ? window.zignagenixAPI : null;
      if (!api?.getActivityUnread) return;
      try {
        const r = await api.getActivityUnread();
        if (!cancelled && r && !r.error) {
          setCount(r.total || 0);
        }
      } catch (_) { /* keep last known count */ }
    };

    poll();
    interval = setInterval(poll, 30000);
    const onVisibility = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (count <= 0) return null;
  return (
    <span className="sidebar-activity-badge" aria-label={`${count} unread`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

function Sidebar({ activePage, onNavigate, user, onLogout, isOpen = false, onClose }) {
  // Task #204 — Mobile drawer support. On desktop the sidebar is always
  // visible; on mobile (≤768px) it slides in/out controlled by `isOpen`.
  // Clicking a nav item closes the drawer so the page is visible.
  const userPermissions = user?.permissions || [];
  const [sections] = useState(DEFAULT_SECTIONS);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleNavigateAndClose = useCallback((pageId) => {
    onNavigate(pageId);
    if (onClose) onClose();
  }, [onNavigate, onClose]);

  // Initials from display name
  const initials = user?.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  // Ref to the nav container for keyboard navigation
  const navRef = useRef(null);

  // Keyboard handler: arrow keys navigate between sidebar buttons
  const handleNavKeyDown = useCallback((e) => {
    // ⚠ Same rule as src/hooks/useArrowNav.js: a modified arrow belongs to the
    // global navigation history (Alt+ArrowLeft = Back, NavHistory.js:242-247),
    // not to sidebar roving focus. Without this, Alt+ArrowLeft moved the
    // sidebar selection AND navigated Back from one keystroke. Return rather
    // than preventDefault so the window-level handler still receives it.
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

    const nav = navRef.current;
    if (!nav) return;

    const buttons = Array.from(nav.querySelectorAll('button.sidebar-item'));
    if (buttons.length === 0) return;

    const currentIndex = buttons.indexOf(document.activeElement);
    let nextIndex = -1;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    if (nextIndex >= 0 && buttons[nextIndex]) {
      buttons[nextIndex].focus();
      buttons[nextIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, []);

  return (
    <nav className={`sidebar ${isOpen ? 'sidebar--open' : ''}`} aria-hidden={!isOpen ? undefined : 'false'}>
      <button
        type="button"
        className="sidebar-brand"
        onClick={() => handleNavigateAndClose('my-day')}
        aria-label="Go to My Day"
      >
        <img src={logo} alt="ZignaGenix" className="sidebar-logo" />
        <div className="sidebar-brand-text">
          <span className="sidebar-title">ZignaGenix</span>
          <span className="sidebar-version" title={APP_BUILT_AT || undefined}>{versionLine()}</span>
        </div>
      </button>

      <div className="sidebar-nav" ref={navRef} role="navigation" aria-label="Main navigation" onKeyDown={handleNavKeyDown}>
        {sections.map((section) => {
          const visibleItems = section.items.filter((item) => {
            // Item visibility supports three shapes:
            //   * permission: null            → always visible
            //   * permission: 'foo:read'      → user must hold that permission
            //   * permissions: ['a','b','c']  → user must hold ANY one of them
            //     (any-of semantics — used by consolidated surfaces like
            //     "Messages" that span multiple legacy permission groups)
            if (item.permission === null) return true;
            if (Array.isArray(item.permissions) && item.permissions.length > 0) {
              return item.permissions.some((p) => userPermissions.includes(p));
            }
            return userPermissions.includes(item.permission);
          });
          if (visibleItems.length === 0) return null;

          /*
           * Alphabetize the visible entries within this section (A→Z,
           * locale-aware, case-insensitive) — DISPLAY ORDER ONLY. Section
           * order, ids, permissions and visibility filtering above are all
           * untouched. Shipped by #233.
           *
           * ── ⛔⛔ UNLESS THE SECTION DECLARES ITS OWN ORDER — Card P114 (F4)
           *
           * ⚠⚠ THIS IS A PARTIAL REVERSAL OF #233, FOR TWO SECTIONS, AND IT
           * IS DELIBERATE. Read this before "tidying" it back.
           *
           * Luisa Parnell (Patient Experience Manager) asked for a specific
           * left-nav order on 23 Oct. Her order is a WORKFLOW order — Patients
           * first, then the two dashboards, then documentation, then the
           * clinical data screens — and a workflow order is not alphabetical
           * and cannot be expressed as one. Reordering DEFAULT_SECTIONS alone
           * would have changed NOTHING on screen, because this sort runs over
           * whatever the array says. (That trap is exactly what the P108
           * triage got wrong when it sized this item: it reported the order as
           * "array order, size S". The array is not the display order.)
           *
           * So a section may now opt out by declaring `ordered: true`, and
           * then its items render in the order written in DEFAULT_SECTIONS.
           * ⚠ OPT-IN, SECTION BY SECTION. Only Clinical and Operations carry
           * it; Overview, Communication, Intelligence and Administration are
           * untouched and stay alphabetical — #233's behaviour survives
           * everywhere it was not explicitly overridden, and
           * tests/react/sidebarAlphabetical.test.js (which drives Intelligence)
           * still guards it.
           *
           * ⚠ ORDER ONLY. Permission filtering has already happened above, so
           * an ordered section can no more reveal an entry than a sorted one;
           * a hidden item simply leaves a gap in the declared sequence.
           */
          const sortedItems = section.ordered
            ? visibleItems
            : [...visibleItems].sort((a, b) =>
              (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));

          return (
            <div key={section.label} className="sidebar-section" role="group" aria-label={section.label}>
              <div className="sidebar-section-label">{section.label}</div>
              {sortedItems.map((item) => {
                const Icon = ICON_MAP[item.iconName] || Settings;
                const isActive = activePage === item.id;
                return (
                  <button
                    key={item.id}
                    className={`sidebar-item ${isActive ? 'sidebar-item--active' : ''}`}
                    onClick={() => handleNavigateAndClose(item.id)}
                    title={item.label}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon size={20} strokeWidth={isActive ? 2.2 : 1.8} />
                    <span className="sidebar-item-label">{item.label}</span>
                    {item.id === 'activity' && <ActivityBadge />}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initials}</div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{user?.displayName || 'Unknown'}</span>
            <span className="sidebar-user-role">{user?.role || 'none'}</span>
          </div>
        </div>
        <button className="sidebar-logout" onClick={() => setShowLogoutConfirm(true)} title="Sign out">
          <LogOut size={16} />
        </button>
      </div>
      {showLogoutConfirm && (
        <LogoutConfirmModal
          onConfirm={onLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </nav>
  );
}

export default Sidebar;

// ⚠ Tests only — see the comment on DEFAULT_SECTIONS.
export const DEFAULT_SECTIONS_FOR_TEST = DEFAULT_SECTIONS;
